// The back office's record page (Task B3, spec §10.3): one invitation's whole story, for "I never got my email" and
// "what happened to my booking?". Decided 2026-11-02 (Jonathan): the codes' hand-offs and the cancellations are steps
// in the one timeline (the same history as the Codes page's History); every email is in the delivery log.
import React, { useEffect, useState } from "react";
import { api } from "../../lib/api.js";
import { usePoll } from "../../lib/usePoll.js";
import { followLink } from "../../lib/router.js";
import { PageHead, StatePill, Notice, fmtWhen } from "./ui.jsx";
import History from "../History.jsx";

export const KIND = { rep: "Lipari employee", guest: "Guest", retailer: "Customer" };
const DELIVERY = { sent: "ok", queued: "mid", failed: "bad", bounced: "bad" };
// A plain date ("2026-12-11") is that calendar day; a moment is shown in Eastern time.
const plain = (v) => /^\d{4}-\d{2}-\d{2}$/.test(String(v));
const day = (v) => new Date(plain(v) ? `${v}T00:00:00Z` : v).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: plain(v) ? "UTC" : "America/Detroit" });

export default function RecordPage({ id }) {
  const [rec, setRec] = useState(null);
  const [error, setError] = useState(null);
  // Refreshes every 10 seconds like the rest of the back office, so an email waiting to send updates on its own.
  useEffect(() => { setRec(null); setError(null); }, [id]);
  const load = () => api(`/backoffice/invitations/${id}/record`).then((r) => { setRec(r); setError(null); }, (err) => setError(err.message));
  usePoll(load, 10000, true, id);
  const back = <a className="bo-link" href="/admin" onClick={followLink("/admin")}>← Invitations</a>;
  if (error && !rec) return <div className="record">{back}<Notice tone="bad">{error}</Notice></div>;
  if (!rec) return <div className="record">{back}<PageHead title="Record" sub="Loading…" /></div>;
  const { invitation: inv, registration: reg } = rec;
  const now = Date.now(), today = new Date().toLocaleDateString("en-CA", { timeZone: "America/Detroit" });
  const allCanceled = reg && !rec.travellers.length;
  return (
    <div className="record">
      {back}
      <PageHead title={inv.name} sub={`${KIND[inv.kind] ?? inv.kind} · ${inv.email} · ${rec.event.name}`} aside={<StatePill state={inv.state} />} />
      <dl className="record-facts">
        <div><dt>Travelers</dt><dd>{rec.travellers.length} of {inv.travellersAllowed}{allCanceled ? " · all canceled" : ""}</dd></div>
        <div><dt>Codes</dt><dd>{rec.codes.length ? <span className="mono">{rec.codes.join(", ")}</span> : "None yet"}{allCanceled && rec.codes.length ? <span className="muted"> (no one on them)</span> : null}</dd></div>
        <div><dt>Registrant's mobile</dt><dd>{rec.registrantPhone || "–"}</dd></div>
        <div><dt>Registered</dt><dd>{reg ? `${fmtWhen(reg.createdAt)} · ${reg.travelMethod === "B" ? "flying" : "driving"}` : "Not yet"}</dd></div>
      </dl>
      {/* The dates ahead are dimmed. */}
      {rec.dates.length > 0 && <div className="record-dates">{rec.dates.map(d => (
        <span key={d.label} className={(plain(d.on) ? d.on >= today : new Date(d.on) > now) ? "ahead" : ""}>{d.label} <b>{day(d.on)}</b></span>))}</div>}

      <h2 className="record-h">Who's registered</h2>
      <div className="bo-table record-who">
        <div className="bo-th"><div>Name</div><div>Email</div><div>Mobile</div><div>Code</div></div>
        {rec.travellers.map((t, i) => (
          <div className="bo-tr" key={i}><div><b>{t.name}</b>{t.registrant && <span className="tag">registrant</span>}</div>
            <div className="mono">{t.email}</div><div>{t.phone ?? ""}</div><div className="mono">{t.code}</div></div>))}
        {rec.removed.map((t, i) => (
          <div className="bo-tr" key={`x${i}`}><div><s>{t.name}</s> <span className="muted">canceled {day(t.canceledAt)}</span></div>
            <div /><div /><div className="mono muted">{t.code}</div></div>))}
        {!rec.travellers.length && <div className="bo-empty">{reg ? "No one is left on this registration." : "Not registered yet."}</div>}
      </div>

      <h2 className="record-h">Timeline</h2>
      <div className="bo-table record-timeline"><History events={rec.timeline} inline /></div>

      <h2 className="record-h">Delivery log</h2>
      <div className="bo-table record-log">
        <div className="bo-th"><div>Queued</div><div>Email</div><div>To</div><div>Status</div></div>
        {rec.deliveries.map((m, i) => (
          <div className="bo-tr" key={i}><div className="muted">{fmtWhen(m.at)}</div>
            <div>{m.subject}{m.error && <div className="err">{m.error}</div>}</div>
            <div className="mono">{m.to}</div>
            <div><span className={`bo-state ${DELIVERY[m.status] ?? ""}`}>{m.status[0].toUpperCase() + m.status.slice(1)}</span></div></div>))}
        {!rec.deliveries.length && <div className="bo-empty">No emails yet.</div>}
      </div>
    </div>
  );
}
